import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * Searchable multi-select with chip rendering. Mirrors Shopify's pattern.
 *
 * Props:
 *  - options: string[] — full list of selectable values
 *  - value: string[] — currently selected values
 *  - onChange: (next: string[]) => void
 *  - placeholder: string — shown when nothing is selected (and required indicator if required)
 *  - required: boolean — adds a red asterisk to placeholder + red border when empty
 *  - disabled: boolean
 *  - testId: data-testid prefix
 */
export default function MultiSelectChips({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  required = false,
  disabled = false,
  testId = "multi-select-chips",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (opt) => {
    if (disabled) return;
    const exists = value.includes(opt);
    const next = exists ? value.filter((v) => v !== opt) : [...value, opt];
    onChange?.(next);
  };

  const remove = (opt) => {
    if (disabled) return;
    onChange?.(value.filter((v) => v !== opt));
  };

  const empty = value.length === 0;
  const showRedBorder = required && empty;

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`min-h-[40px] w-full px-2 py-1.5 flex items-center gap-1 flex-wrap rounded-md border bg-white text-left text-sm transition-colors ${
            showRedBorder ? "border-red-300 focus:ring-red-200" : "border-[#E8E8E8] hover:border-slate-300"
          } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          data-testid={`${testId}-trigger`}
        >
          {empty && (
            <span className="text-muted-foreground text-[12px] px-1">
              {placeholder}{required ? " *" : ""}
            </span>
          )}
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-[#E1F5EE] text-[#085041] border border-[#A5DDC9] px-2 py-0.5 text-[11px] font-medium"
              data-testid={`${testId}-chip-${v}`}
            >
              {v}
              {!disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => { e.stopPropagation(); remove(v); }}
                  className="hover:text-red-700 cursor-pointer"
                  data-testid={`${testId}-remove-${v}`}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
            </span>
          ))}
          <ChevronDown className="h-3.5 w-3.5 ml-auto opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center gap-2 px-2 py-2 border-b border-[#E8E8E8]">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories…"
            className="flex-1 text-xs outline-none bg-transparent"
            data-testid={`${testId}-search`}
            autoFocus
          />
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange?.([])}
              className="text-[10px] text-slate-500 hover:text-red-600"
              data-testid={`${testId}-clear-all`}
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-[260px] overflow-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-[11px] text-muted-foreground px-3 py-3 text-center">No matches</div>
          ) : (
            filtered.map((opt) => {
              const selected = value.includes(opt);
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => toggle(opt)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                    selected ? "bg-accent/10 text-accent font-medium" : "hover:bg-slate-50 text-slate-700"
                  }`}
                  data-testid={`${testId}-option-${opt}`}
                >
                  <span className="w-3.5 inline-flex justify-center">
                    {selected ? <Check className="h-3.5 w-3.5" /> : null}
                  </span>
                  <span>{opt}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
